// Help text for: Historical > Overview.
// Keys are referenced as "historical.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it

// Peak window used by every peak / off-peak split on this tab (server rule).
const PEAK_WINDOW =
  "Peak hours are fixed for you: 5-9 pm in Dec-Feb, 6-10 pm in Mar-May and Sep-Nov, 7-11 pm in Jun-Aug; every other hour counts as off-peak.";

const help = {
  // ===== Top filter bar =====
  dateRange: {
    title: "Date Range",
    use: "The stretch of time the graph, summary cards and heatmap cover. Set the start and end, then press Apply.",
    source: "input",
    how: "Comes pre-filled with the last 7 full days ending yesterday (midnight seven days back to 23:59:59 yesterday). In MINUTE resolution it jumps to the last 24 hours and cannot stretch beyond 24 hours; in SECOND resolution it covers one hour from the start you pick. The charts further down follow the same stretch, except in SECOND resolution where they keep the one before.",
  },
  quickRange: {
    title: "Quick Range (1D / 3D / 7D / 30D)",
    use: "Shortcut that fills the date range with the last 1, 3, 7 or 30 full days.",
    source: "input",
    how: "Each button ends at 23:59:59 yesterday and starts at midnight that many days earlier, so today is never included. Press Apply to load it. Hidden in SECOND and MINUTE resolution.",
  },
  machineSelect: {
    title: "Machine",
    use: "Picks which meter the graph, summary cards and heatmap describe.",
    source: "input",
    how: "Comes pre-filled with Usage (Total Consumption), which adds up all your grid meters; it is offered only if you have a grid meter. The choices follow the Branch selector, and picking a branch adds an All Machines option. Solar, grouping and branch items are not offered here. Press Apply to load.",
  },
  metricSelect: {
    title: "Metric",
    use: "Chooses which reading is drawn and summarised for the machine you picked.",
    source: "input",
    how: "Comes pre-filled with Power (kW). The choices are the readings saved from the meter: Power, Energy (kWh used per interval), Voltage, Power Factor, Current, Frequency, THD Voltage, THD Current and TAP (the total active power the meter reports). Hidden while Usage is picked. Press Apply to load.",
  },
  currencyMode: {
    title: "Energy / PKR",
    use: "Switches the values that can carry a price between energy units and money.",
    source: "input",
    how: "Starts on Energy. PKR multiplies kW, kWh and TAP by your unit price (PKR per kWh): the grid price, or the generator price when a generator is picked; breakdown charts price each source at its own rate. Voltage, current, power factor, frequency and THD stay in their own units. Prices are set under Info > Details > Unit Prices; if no generator, solar or peak price is set, the grid price is used.",
  },

  // ===== Summary cards =====
  netEnergy: {
    title: "Net Energy",
    use: "Energy taken from the grid less the energy sent back, over the chosen dates.",
    source: "backend",
    how: "Worked out from the meter's energy counters: kWh brought in minus kWh sent out, adding up how far each counter moved hour by hour (a counter that rolls back to zero is corrected, not treated as a drop). For Usage, all grid meters are added together.",
  },
  netCost: {
    title: "Net Cost",
    use: "What the grid energy over these dates cost, after credit for the units you sent back.",
    source: "calculated",
    how: "Imported kWh times the grid unit price, minus exported kWh times the export credit rate. The credit rate is your solar unit price, or the grid price when no solar price is set. Prices come from Info > Details > Unit Prices.",
  },
  importEnergy: {
    title: "Import",
    use: "Energy taken from the grid over the chosen dates.",
    source: "backend",
    how: "How far the meter's consumed-kWh counter climbed, added up hour by hour. In PKR mode it is multiplied by the grid unit price.",
  },
  exportEnergy: {
    title: "Export",
    use: "Energy pushed back to the grid over the chosen dates — surplus solar, for example.",
    source: "backend",
    how: "How far the meter's export-kWh counter climbed, added up hour by hour. In PKR mode it is multiplied by the export credit rate (your solar unit price, or the grid price when none is set).",
  },
  consumption: {
    title: "Consumption",
    use: "Energy the machine you picked used over the chosen dates.",
    source: "backend",
    how: "Consumed kWh less any exported kWh, taken from how far the meter's energy counters moved each hour.",
  },
  generation: {
    title: "Generation",
    use: "Energy the generator you picked produced over the chosen dates.",
    source: "backend",
    how: "Whichever of the meter's two energy counters climbed more is used, because wiring direction differs from site to site, and its hourly rise is added up. The wording on the card follows the machine's type, or words like 'gen', 'solar' or 'pv' in its name.",
  },
  totalCost: {
    title: "Total Cost",
    use: "The energy card priced in money while PKR mode is on.",
    source: "calculated",
    how: "The energy for these dates in kWh, times the unit price: the generator price for a generator, otherwise the grid price, from Info > Details > Unit Prices.",
  },
  estimatedCost: {
    title: "Estimated Cost",
    use: "A rough money figure for the energy card, shown in both Energy and PKR mode.",
    source: "calculated",
    how: "Net energy — or generated energy for a generator — times one single unit price: the generator price for a generator, otherwise the grid price. Unlike Net Cost, it does not credit exported units separately.",
  },
  avgMetric: {
    title: "Average",
    use: "The typical level of the reading you picked over the chosen dates.",
    source: "backend",
    how: "The mean of every value at the resolution you picked (hourly values in HOUR, daily values in DAY). For Usage, grid meters are added together at each moment first. In DAY resolution, power and current are the day's hourly values added up, while voltage, power factor, frequency and THD are daily averages.",
  },
  minMetric: {
    title: "Minimum",
    use: "The lowest value of the reading you picked over these dates, and when it happened.",
    source: "backend",
    how: "The smallest value at the resolution you picked, from the same numbers as the Average card. The date and time underneath is when it occurred.",
  },
  maxMetric: {
    title: "Maximum",
    use: "The highest value of the reading you picked over these dates, and when it happened.",
    source: "backend",
    how: "The largest value at the resolution you picked, from the same numbers as the Average card. The date and time underneath is when it occurred.",
  },
  mdi: {
    title: "MDI (Maximum Demand Indicator)",
    use: "The highest load you held without a break for 30 minutes over these dates — the figure utilities use for demand charges.",
    source: "backend",
    how: "The readings are lined up minute by minute (grid meters added together for Usage, and gaps of up to 5 minutes filled in), the lowest value in every 30-minute window is found, and the biggest of those is reported. If that comes out as 0, the single highest reading is used instead. The time shown is when that window ended.",
  },
  avgMonthlyPf: {
    title: "AVG Monthly PF",
    use: "How well this grid connection used power over the past month; a low power factor can bring utility penalties.",
    source: "backend",
    how: "The average of every hourly power-factor reading for this meter over the last 30 days up to now, whatever dates you set above. Green at 0.9 or higher, red below 0.9, N/A when there is nothing to average. Only shown when a grid meter is picked.",
  },

  // ===== Main graph controls =====
  selectAllNone: {
    title: "ALL / NONE",
    use: "Puts every machine on the graph, or clears it so you can add machines one at a time.",
    source: "input",
    how: "ALL picks every machine in the list under the graph (following the Branch selector); NONE clears them. Your pick stays until you change it or press Apply.",
  },
  phaseMode: {
    title: "Phase",
    use: "Shows the machine's reading for the whole connection or for each electrical phase.",
    source: "input",
    how: "Starts on Overall. 1st/2nd/3rd Phase draws that phase's own reading; All Phases draws all three. Offered only when exactly one three-phase machine is picked, and not used for the Energy reading.",
  },
  gridEnergyFlow: {
    title: "Grid Energy Flow",
    use: "For grid meters on the Energy reading, picks which direction of energy is drawn.",
    source: "input",
    how: "Starts on TOTAL, the meter's usual energy line. IMPORT draws the kWh taken from the grid in each interval; EXPORT draws the kWh sent back, from the meter's export counter. Shown only when every machine on the graph is a grid meter.",
  },
  analysisView: {
    title: "Analysis",
    use: "A graph of the reading you picked over time, for the machines you picked.",
    source: "measured",
    how: "Draws the saved meter readings at the resolution you picked. With a single machine on a line graph, dashed Max, Average and Min lines are added from the summary cards. Very long stretches are thinned to about 2,000 points. In PKR mode, power and energy are multiplied by your unit price.",
  },
  heatmapView: {
    title: "Heatmap",
    use: "Shows which hours of which weekdays the machine usually runs highest and lowest.",
    source: "calculated",
    how: "Each square is the average of the hourly readings that fall on that weekday and hour inside the chosen dates (Usage adds all grid meters together). The colour runs from the lowest square to the highest, as shown in the legend.",
  },
  resolution: {
    title: "Resolution (SECOND / MINUTE / HOUR / DAY)",
    use: "How finely the data is cut up in time.",
    source: "input",
    how: "Starts on HOUR. SECOND shows one hour second by second, MINUTE up to 24 hours minute by minute. DAY groups the hourly data into days: energy is the day's counter rise, power and current are the hourly values added up, and everything else is the daily average. SECOND and MINUTE are not available on the BAR graph.",
  },
  graphType: {
    title: "LINE / BAR",
    use: "Draws the graph as lines or as bars.",
    source: "input",
    how: "Starts on LINE. The Energy reading is always drawn as bars, and BAR is not available in SECOND or MINUTE resolution.",
  },
  selectedMachines: {
    title: "Selected Machines",
    use: "The machines drawn on the graph right now; click a name to add or drop it.",
    source: "input",
    how: "Starts with the machine picked in the filter bar at the top (Usage unless you change it). The number is how many are picked. The choices follow the Branch selector and leave out solar, grouping and branch items.",
  },

  // ===== Energy report (PDF) =====
  pdfReport: {
    title: "Energy Report (PDF)",
    use: "Downloads a PDF report for the machine picked on this screen.",
    source: "calculated",
    how: "The report holds total energy by facility, section, branch and machine; energy used and generated each day; an estimated electricity cost; an energy flow diagram; and a weekday-by-hour energy heatmap. Built from the saved hourly energy for the dates you choose in the window that opens.",
  },
  pdfScope: {
    title: "Scope",
    use: "Which part of the site the report covers.",
    source: "input",
    how: "Follows the machine picked in the filter bar at the top. Usage covers the whole facility, or the branch you picked; any other machine limits the report to that machine.",
  },
  pdfQuickRange: {
    title: "Report Range (7D / 30D / 3M)",
    use: "Shortcut that fills in the report dates.",
    source: "input",
    how: "Each button ends yesterday: 7D is the last 7 days, 30D the last 30 days, 3M the last 3 months. When the window first opens, the dates are copied from this screen's date range instead.",
  },
  pdfDates: {
    title: "From / To Date",
    use: "The days the report covers.",
    source: "input",
    how: "Whole days only: From starts at midnight and To ends at 23:59:59. Comes pre-filled from this screen's date range. To cannot be later than today, and From cannot be after To.",
  },

  // ===== Peak / Off-Peak, ON/OFF, By Source card =====
  peakOffPeakTab: {
    title: "Peak / Off-Peak",
    use: "Splits each machine's energy into peak and off-peak hours, which are billed at different rates.",
    source: "backend",
    how: `Each machine's hourly rise in consumed kWh is added up, and every hour is put in either the peak or the off-peak pile. ${PEAK_WINDOW} It uses this screen's date range; in PKR mode, peak kWh are priced at your peak unit price and off-peak kWh at the grid price.`,
  },
  peakEnergy: {
    title: "Peak",
    use: "Energy used during peak hours.",
    source: "backend",
    how: `The hourly rise in consumed kWh, added up for the hours inside the peak window. ${PEAK_WINDOW}`,
  },
  offPeakEnergy: {
    title: "Off-Peak",
    use: "Energy used outside peak hours.",
    source: "backend",
    how: `The hourly rise in consumed kWh, added up for the hours outside the peak window. ${PEAK_WINDOW}`,
  },
  onOffTab: {
    title: "ON / OFF",
    use: "How long each machine, grid feed or generator ran versus sat idle over these dates, and how often it switched.",
    source: "backend",
    how: "Every minute of the period is checked: a machine counts as ON for that minute when its power is above 0.05 kW (0.5 kW for generators), and minutes with no reading count as OFF. Switches from OFF to ON and from ON to OFF are counted too.",
  },
  hoursOn: {
    title: "Hours ON",
    use: "Total running time over the chosen dates.",
    source: "backend",
    how: "The number of minutes with power above 0.05 kW (0.5 kW for generators), shown as hours and minutes. A row with no meter of its own adds up the machines beneath it.",
  },
  hoursOff: {
    title: "Hours OFF",
    use: "Total idle time over the chosen dates.",
    source: "calculated",
    how: "Minutes at or below the ON threshold, plus minutes with no reading at all. If ON and OFF together fall short of the whole period, OFF is topped up to the length of the period minus ON.",
  },
  bySourceTab: {
    title: "By Source",
    use: "Shows how much of each machine's energy came from the grid, from solar and from a generator.",
    source: "backend",
    how: "For each hour, the solar and generator output of the machine's branch is shared among that branch's machines in proportion to how much each used; whatever is left counts as grid (branch load less solar less generator, never below zero). Only machines that consume power are listed.",
  },
  sourceGrid: {
    title: "Grid",
    use: "The part of the machine's energy that came from the grid.",
    source: "backend",
    how: "The machine's use each hour times the grid's share of that hour's supply, added up over the chosen dates. In PKR mode it is priced at the grid unit price.",
  },
  sourceSolar: {
    title: "Solar",
    use: "The part of the machine's energy that came from solar panels in the same branch.",
    source: "backend",
    how: "The machine's use each hour times solar's share of that hour's supply, added up over the chosen dates. In PKR mode it is priced at the solar unit price (the grid price when none is set).",
  },
  sourceGenset: {
    title: "Genset",
    use: "The part of the machine's energy that came from generators in the same branch.",
    source: "backend",
    how: "The machine's use each hour times the generators' share of that hour's supply, added up over the chosen dates. In PKR mode it is priced at the generator unit price (the grid price when none is set).",
  },

  // ===== Breakdown charts =====
  energyBreakdown: {
    title: "Energy Breakdown",
    use: "A pie chart of how energy is shared between the parts of your site; click a slice to open it up.",
    source: "backend",
    how: "Each slice is one item's net energy (consumed less exported kWh) over the chosen dates, from its hourly counters. An item with no meter of its own adds up its children; where a meter reads more than the meters beneath it, the gap shows as '<name> - Others'. In PKR mode each slice is priced at its own source's rate.",
  },
  energyFlow: {
    title: "Energy Flow Diagram",
    use: "Shows how energy travels from the main supply down through sections and machines.",
    source: "backend",
    how: "Each band carries the receiving item's net energy for the chosen dates — the same numbers as the Energy Breakdown. Very small flows are drawn at a minimum thickness of 20% of the largest flow so they stay visible; hover a band to see the true value. In PKR mode each flow is priced at the receiving item's rate.",
  },
  flowLevels: {
    title: "Levels",
    use: "How many levels of your site hierarchy the flow diagram shows.",
    source: "input",
    how: "Starts on Full, meaning every level there is. Pick a smaller number to hide the deeper machines.",
  },

  // ===== Solar section =====
  solarMachine: {
    title: "Solar Machine",
    use: "Chooses which solar installation the solar cards and graphs describe.",
    source: "input",
    how: "Lists the solar meters on your account, following the Branch selector; the first one is picked for you.",
  },
  solarDateRange: {
    title: "Solar Date Range",
    use: "The stretch of time the solar cards and graphs cover.",
    source: "input",
    how: "Starts out matching the dates set above. The 1D/3D/7D/30D buttons load full days ending yesterday straight away; if you type dates yourself, press Apply.",
  },
  solarGridExport: {
    title: "Total Grid Export",
    use: "Energy pushed back into the grid through the grid meter tied to this solar installation.",
    source: "backend",
    how: "Every hourly reading on that grid meter that runs negative (flowing out) is added up, each one counted as a full hour, to give kWh. Shows 0 when no grid meter is tied to this solar meter. In PKR mode it is priced at the export credit rate.",
  },
  solarGridImport: {
    title: "Total Grid Import",
    use: "Energy drawn from the grid through the grid meter tied to this solar installation.",
    source: "backend",
    how: "Every positive hourly reading on that grid meter is added up, each one counted as a full hour, to give kWh. In PKR mode it is priced at the grid unit price.",
  },
  solarProduction: {
    title: "Total Solar Production",
    use: "Energy the solar installation you picked actually generated.",
    source: "backend",
    how: "The hour-by-hour rise in the solar meter's energy counter, added up (whichever direction climbed more is used). The same figure as Actual Solar Energy Production further down. In PKR mode it is priced at the solar unit price.",
  },
  loadConsumption: {
    title: "Total Load Consumption",
    use: "All the energy the site used while this solar installation was running, from every source.",
    source: "backend",
    how: "Grid import plus generator output plus solar output, each added up from the hourly power readings with every reading counted as a full hour. In PKR mode the whole figure is priced at the grid unit price.",
  },
  solarImportExport: {
    title: "Solar Import / Export",
    use: "Shows, hour by hour, the energy sent to the grid and taken from the grid at this installation's grid meter.",
    source: "measured",
    how: "Hourly grid-meter readings split by direction: below zero is 'Feed to Grid (Export)', above zero is 'Receive from Grid (Import)'. The Heatmap view shows average solar output per weekday and hour instead.",
  },
  solarIdealVsActual: {
    title: "Solar Energy Production: Ideal vs Actual",
    use: "Compares what the panels did produce with what they should have produced in the day's sunlight.",
    source: "calculated",
    how: "Actual is the hourly rise in the solar meter's energy counter. Ideal is the hour's sunlight for the plant's city (from a weather service, in MJ per m²) divided by 3.6, times the panel capacity in kW, times 3.703 m² per kW, times 20% panel efficiency. The panel capacity and city come from the solar machine's settings.",
  },
  actualSolar: {
    title: "Actual Solar Energy Production",
    use: "The energy the solar installation really produced over the solar dates.",
    source: "backend",
    how: "The hourly rise in the solar meter's energy counter, added up as kWh. In PKR mode it is multiplied by the solar unit price.",
  },
  idealSolar: {
    title: "Ideal Solar Energy Production",
    use: "The energy the installation should have produced, given the sunlight recorded for your city.",
    source: "backend",
    how: "Hourly estimates added up: the city's sunlight for that hour (MJ per m²) divided by 3.6, times the panel capacity in kW, times 3.703 m² per kW, times 20% efficiency. It relies on the panel capacity and city saved for the solar machine.",
  },
  solarProductivity: {
    title: "Productivity",
    use: "How close actual solar output came to the ideal; 100% means the panels delivered everything expected.",
    source: "calculated",
    how: "Actual Solar Energy Production divided by Ideal Solar Energy Production, as a percentage. Shows -- when there was no actual production.",
  },

  // ===== Day comparison graphs =====
  comparisonGraph: {
    title: "Day Comparison",
    use: "Lays one machine's hourly power for several days on top of each other, so you can compare them hour by hour.",
    source: "measured",
    how: "Each line is one day you picked: 24 hourly power readings in kW, starting at the start time you set. In PKR mode the line is those kW times the machine's unit price, so it reads as spend per hour.",
  },
  comparisonDates: {
    title: "Dates",
    use: "The days to compare; each day you pick becomes one line.",
    source: "input",
    how: "Comes pre-filled with yesterday. Click days in the calendar to add or drop them (days in the future cannot be picked), then press Search.",
  },
  comparisonStartTime: {
    title: "Start Time",
    use: "The hour each compared day begins, so a day can run from 06:00 to 06:00 the next morning, for example.",
    source: "input",
    how: "Comes pre-filled with 00:00:00. Each day then covers the 24 hours from that time. Press Search to apply.",
  },
  comparisonLoad: {
    title: "Load",
    use: "The machine whose days are being compared.",
    source: "input",
    how: "Comes pre-filled with the first machine in the list other than Usage. Branch and grouping entries add up the machines beneath them.",
  },
  comparisonDate: {
    title: "Date",
    use: "The day this summary row describes.",
    source: "input",
    how: "One row for each day you picked.",
  },
  comparisonEnergy: {
    title: "Energy",
    use: "The machine's net energy over that day's 24-hour window.",
    source: "backend",
    how: "Consumed less exported kWh, from how far the meter's energy counters moved each hour. In PKR mode it is multiplied by the machine's unit price.",
  },
  comparisonCost: {
    title: "Estimated Cost (PKR)",
    use: "Roughly what that day's energy cost.",
    source: "calculated",
    how: "Energy in kWh times the unit price for the machine's type (grid, generator or solar), from Info > Details > Unit Prices.",
  },
  comparisonPeak: {
    title: "Peak",
    use: "Energy used during that day's peak hours.",
    source: "backend",
    how: `The hourly rise in consumed kWh, added up for the hours inside the peak window. ${PEAK_WINDOW} In PKR mode it is priced at the peak unit price.`,
  },
  comparisonOffPeak: {
    title: "Off-Peak",
    use: "Energy used outside that day's peak hours.",
    source: "backend",
    how: `The hourly rise in consumed kWh, added up for the hours outside the peak window. ${PEAK_WINDOW} In PKR mode it is priced at the grid unit price.`,
  },
  comparisonMaxPower: {
    title: "Max Power",
    use: "The machine's highest hourly power reading on that day.",
    source: "calculated",
    how: "The largest of the 24 hourly power values (kW) drawn on the graph. Always in kW, even in PKR mode.",
  },
  comparisonMinPower: {
    title: "Min Power",
    use: "The machine's lowest hourly power reading on that day.",
    source: "calculated",
    how: "The smallest of the 24 hourly power values (kW) drawn on the graph, skipping hours with no reading. Always in kW.",
  },

  // ===== Chart menu exports =====
  exportImagePdf: {
    title: "PDF",
    use: "Saves a picture of this chart as a PDF file.",
    source: "calculated",
    how: "Captures the chart exactly as it looks on screen now — same dates, machines and units.",
  },
  exportCsv: {
    title: "CSV",
    use: "Downloads the numbers behind this chart as a plain text file a spreadsheet can open.",
    source: "calculated",
    how: "Exports the values drawn right now, in the units on screen (kWh, kW or PKR), for the dates and machines you have picked.",
  },
  exportXls: {
    title: "XLS",
    use: "Downloads the numbers behind this chart as an Excel workbook.",
    source: "calculated",
    how: "The same values as the CSV download, on one sheet named 'Chart Data'.",
  },
  exportCustom: {
    title: "Custom Export",
    use: "Downloads raw meter data for the dates, resolution, machine and reading you choose, whatever the chart happens to show.",
    source: "input",
    how: "Opens a short form, then downloads a CSV file built fresh from the saved readings.",
  },
  customExportMode: {
    title: "Export Granularity",
    use: "The time step of the rows you get.",
    source: "input",
    how: "Starts on Hour. Second allows 24 hours at most, and Minute 30 days at most. Day gives one row per day.",
  },
  customExportRange: {
    title: "Start / End Date & Time",
    use: "The stretch of time to download.",
    source: "input",
    how: "Comes pre-filled with the last 24 hours up to now. Start must come before End.",
  },
  customExportMachine: {
    title: "Target Machine",
    use: "Whose meter data to download.",
    source: "input",
    how: "Starts on All, which downloads the Usage total (all grid meters added together) rather than every machine one by one. Pick a machine to get only its columns.",
  },
  customExportMetric: {
    title: "Target Metric",
    use: "Which reading to download.",
    source: "input",
    how: "Starts on Power (kW). The values are the saved meter readings at the step you picked.",
  },
};

export default help;
